// Root shell components extracted from router.tsx (health-tour 2, P2-4 /
// F-3): router.tsx exports non-component values (router, route tree,
// IS_START_COMPANY_BUNDLE), so any component defined there breaks react-refresh
// (4 only-export-components errors). This file exports ONLY components.
import { lazy, Suspense, useEffect, useState } from 'react'
import { useNavigate, useRouterState, Outlet } from '@tanstack/react-router'
import { USE_REAL_API } from './lib/api'
import { isStartCompanyBundle } from './lib/public-bundle'
import { crossOriginRedirect } from './lib/origin-shell'
import { useAuthStore } from './store/auth'
import { fetchMe, shellForOrgType } from './lib/auth'
import { useRealAuthGuard } from './useRealAuthGuard'
import { PlatformViewBanner } from './components/PlatformViewBanner'
import { Toaster } from './components/ui/toaster'
import { RouteLoadingOverlay } from './components/loading/RouteLoadingOverlay'
import { PostLoginGates } from './features/onboarding/PostLoginGates'
import ProblemReporter from './features/problem-reporting/admin/AdminProblemReporter'
import { useProblemReportHost } from './lib/problem-reporting/host'
import { usePageViewTracker } from './lib/page-view-tracker'
import { useT } from './i18n'
import { sharedUiI18n } from './i18n/components/shared-ui'

const ProblemReportDesignPreview = import.meta.env.DEV
  ? lazy(() => import('./features/problem-reporting/ProblemReportDesignPreview'))
  : null

// Local (non-exported) copy of the bundle flag — router.tsx exports the
// canonical IS_START_COMPANY_BUNDLE for route wiring; importing it here would
// be circular. isStartCompanyBundle() is pure, so both read the same value.
const IS_START_COMPANY_BUNDLE = isStartCompanyBundle()

export function RootLayout() {
  const { blockAppShell } = useRealAuthGuard()
  const t = useT(sharedUiI18n).rootShell
  usePageViewTracker()
  useProblemReportHost()
  const reportRouteId = useRouterState({ select: state => state.matches.at(-1)?.routeId ?? '' })
  const unifiedHelpLauncher = /^\/(?:admin|seller|supply)(?:\/|$)/.test(
    reportRouteId,
  )
  // Public wizard bundle: render ONLY the wizard — no sidebar/shell, no platform
  // banners, and no PostLoginGates (those hit authenticated APIs and belong to
  // the signed-in app, never the anonymous public intake page).
  if (IS_START_COMPANY_BUNDLE) {
    return (
      <div className="min-h-screen bg-white">
        <Outlet />
        <Toaster />
      </div>
    )
  }
  // Reibul-style split: each shell owns its own row (Sidebar | main + ContentTopbar).
  // RootLayout only stacks the global read-only-viewing banner above
  // whatever shell (or public page) renders in the Outlet.
  return (
    <div className="flex h-screen flex-col overflow-hidden">
      <PlatformViewBanner />
      {blockAppShell ? (
        // A seller/supply mismatch is still being resolved (or was just found and
        // is about to be corrected) — the wrong shell's chrome is never painted.
        <div className="p-8 text-sm text-slate-500">{t.loadingDashboard}</div>
      ) : (
        <Outlet />
      )}
      <RouteLoadingOverlay />
      <PostLoginGates />
      {!blockAppShell &&
        !(import.meta.env.DEV && new URLSearchParams(window.location.search).has('reportPreview')) && (
          <ProblemReporter
            routeId={reportRouteId}
            showLauncher={!unifiedHelpLauncher}
          />
        )}
      {ProblemReportDesignPreview && (
        <Suspense fallback={null}><ProblemReportDesignPreview /></Suspense>
      )}
      <Toaster />
    </div>
  )
}

// Landing at the app root. In real-engine mode a logged-in user is bounced to
// their D-7 shell (same role-aware landing as LoginPage) so /app/ never dead-ends
// on the maket hint; without a token the auth guard already forces /login. In
// maket/simulation mode the mock role-switcher drives, so show its hint.
export function IndexLanding() {
  const navigate = useNavigate()
  const t = useT(sharedUiI18n).rootShell
  const accessToken = useAuthStore((s) => s.accessToken)
  const clearTokens = useAuthStore((s) => s.clearTokens)
  const [resolveError, setResolveError] = useState(false)

  useEffect(() => {
    if (!USE_REAL_API || !accessToken) return
    let cancelled = false
    void fetchMe().then((me) => {
      if (cancelled) return
      // No /me at all = the stored token is stale/expired. Drop it and re-login
      // rather than stranding the user on a dead landing page.
      if (!me) {
        clearTokens()
        navigate({ to: '/login', replace: true })
        return
      }
      const shell = shellForOrgType(me.active_membership?.org_type)
      if (!shell) {
        setResolveError(true)
        return
      }
      // The account's shell lives on the OTHER origin (e.g. an admin session left
      // in app.pexify.net storage from before the origin split). Drop the token —
      // it does not belong on this origin — and land on /login HERE. Routing to
      // the foreign shell would mount and flash its dashboard; bouncing to the
      // other origin would make this origin's login page unreachable.
      if (crossOriginRedirect(shell)) {
        clearTokens()
        navigate({ to: '/login', replace: true })
        return
      }
      navigate({ to: shell, replace: true })
    })
    return () => {
      cancelled = true
    }
  }, [accessToken, navigate, clearTokens])

  if (USE_REAL_API) {
    return (
      <div className="p-8 text-sm text-slate-500">
        {resolveError ? (
          <>
            {t.accountLoadError}{' '}
            <button
              type="button"
              onClick={() => navigate({ to: '/login' })}
              className="font-medium text-brand-600 hover:underline"
            >
              {t.signInAgain}
            </button>
          </>
        ) : (
          t.loadingDashboard
        )}
      </div>
    )
  }

  // The "Acting as" switcher lives in the SHELL topbars (/admin | /seller |
  // /supply) — it does not exist on this bare landing, so a text hint pointing
  // at it dead-ends a fresh visitor (hit live 2026-08-13). Offer the three
  // shells directly instead.
  return (
    <div className="p-8 text-slate-600">
      <p className="mb-3 text-sm">{t.pickRole}</p>
      <div className="flex gap-2">
        {(
          [
            ['Admin', '/admin'],
            ['Seller', '/seller'],
            ['Supply', '/supply'],
          ] as const
        ).map(([label, to]) => (
          <button
            key={to}
            type="button"
            onClick={() => navigate({ to })}
            className="rounded-md bg-white px-4 py-2 text-sm font-medium text-slate-700 ring-1 ring-slate-200 transition-all hover:ring-slate-300 hover:shadow-sm"
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  )
}
